import React, { useContext, useEffect } from 'react';
import { useParams, useLocation, Outlet } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';

import { TARGET_CATEGORIES_MAP } from 'constants/categories';
import { EVENTS } from 'constants/events';
import {
  FEATURE_FLAGS,
  EXPIRING_FUNDS_ON_CONTROL_BANNER_SEGMENTS,
} from 'constants/featureFlag';
import CurrentUserContext from 'contexts/CurrentUserContext';
import FeatureFlagContext from 'contexts/FeatureFlagContext';

import useQueryCategoriesWithProducts from 'hooks/useQueryCategoriesWithProducts';

import { logEvent } from 'utils/amplitude';
import { selectDefaultWallet } from 'utils/employee';
import { getExpiringFundsTotalAndDays, getWalletsExpiring } from 'utils/wallet';

import Banner from './Banner';

import ExpiringFundsBanner from 'components/common/ExpiringFundsBanner';

import { EmployeeWallet } from './types';
import { BANNER_TYPE } from 'constants/products';

const SEARCH_PATH = '/search';

const StoreWithBannerLayout = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const feature_flag = useContext(FeatureFlagContext);
  const location = useLocation();

  const expiring_funds_flag_v2 = feature_flag.flag(
    FEATURE_FLAGS.EXPIRING_FUNDS_BANNER_V2,
    'off'
  );
  const store_landing_page_variation = feature_flag.flag(
    FEATURE_FLAGS.STORE_LANDING_PAGE,
    'off'
  );

  const { employee } = useContext(CurrentUserContext);
  const { employee_wallet_id } = useParams<{ employee_wallet_id: string }>();
  const initial_search_query =
    new URLSearchParams(location.search).get('q') ?? '';

  const handleError = useErrorHandler();

  const [search_query, setSearchQuery] = React.useState(initial_search_query);
  useEffect(() => {
    if (location.pathname !== SEARCH_PATH) {
      setSearchQuery('');
    }
  }, [location.pathname]);

  const {
    settings: { currency, country },
  } = employee;

  const employee_wallets =
    employee?.employee_wallets.filter(
      ({
        company_wallet_configuration: { is_twic_store_enabled },
      }: EmployeeWallet) => is_twic_store_enabled
    ) || [];

  const employee_wallet = employee_wallet_id
    ? employee_wallets.find(
        ({ id }: EmployeeWallet) => id === employee_wallet_id
      )
    : selectDefaultWallet({ employee_wallets }) || {};

  const { data: categories_data } = useQueryCategoriesWithProducts({
    employee_wallets,
    country,
    onError: (error: Error) => handleError(error),
  });

  const categories = categories_data
    ? categories_data.map((result) => ({
        id: result.id,
        name: result.name,
        alias: result.alias,
      }))
    : [];

  const category_ids = categories.map((category) => category.id);

  const matched_categories = Object.keys(TARGET_CATEGORIES_MAP).reduce(
    (
      results: Array<{
        id: string;
        name: string;
        alias?: string;
      }>,
      target_category
    ) => {
      const index = category_ids.indexOf(target_category);
      return index >= 0 ? [...results, categories[index]] : results;
    },
    []
  );

  const onCategoryCardClick = ({
    event,
    category_filter,
  }: {
    event: React.MouseEvent;
    category_filter: string;
  }) => {
    logEvent(EVENTS.StoreViewed, {
      category_filter,
      category_tile_clicked: true,
      homepage_experiment: true,
    });
  };
  const show_cards = categories.length >= 3 && location.pathname === '/explore';

  const wallets_expiring = getWalletsExpiring(employee_wallets);

  const { expiring_funds_days_left, expiring_funds_total } =
    getExpiringFundsTotalAndDays(wallets_expiring);

  const show_expiring_funds_banner =
    expiring_funds_flag_v2 &&
    EXPIRING_FUNDS_ON_CONTROL_BANNER_SEGMENTS.includes(
      expiring_funds_flag_v2
    ) &&
    expiring_funds_total > 0 &&
    expiring_funds_days_left <= 10;

  return (
    <>
      {show_expiring_funds_banner && (
        <ExpiringFundsBanner
          currency={currency}
          days_left={expiring_funds_days_left}
          total={expiring_funds_total}
          wallets_expiring={wallets_expiring}
          variation={expiring_funds_flag_v2}
          banner_type={BANNER_TYPE.STORE}
        />
      )}
      <Banner
        categories={matched_categories}
        employee_wallet_id={employee_wallet!.id}
        query={search_query}
        show_cards={show_cards}
        show_new_search_banner={store_landing_page_variation === 'landing'}
        onCategoryCardClick={onCategoryCardClick}
        setSearchQuery={setSearchQuery}
      />
      {children ?? <Outlet />}
    </>
  );
};

export default StoreWithBannerLayout;
